// src/services/Atelier5Service.js
import api from '../api/apiClient';

/**
 * Service for handling Atelier 5 components - Risk Treatment
 */
class Atelier5Service {
  /**
   * UNIFIED: Load Workshop 5 data from single endpoint
   * @param {string} analysisId - Analysis ID
   * @returns {Promise<Object>} - Unified Workshop 5 data
   */
  async loadWorkshop5Unified(analysisId) {
    try {
      console.log('[Atelier5Service] Calling unified endpoint:', `/analyses/${analysisId}/workshop5`);
      const response = await api.get(`/analyses/${analysisId}/workshop5`);
      const result = response.data || response;
      console.log('[Atelier5Service] Unified response:', result);
      return result;
    } catch (error) {
      console.error('[Atelier5Service] Unified endpoint failed:', error.message);
      throw error;
    }
  }

  async loadWorkshop5WithAutoEco(analysisId) {
    try {
      console.log('[Atelier5Service] Calling auto-eco endpoint:', `/analyses/${analysisId}/workshop5-with-eco`);
      const response = await api.get(`/analyses/${analysisId}/workshop5-with-eco`);
      const result = response.data || response;
      console.log('[Atelier5Service] Auto-eco response:', result);
      return result;
    } catch (error) {
      console.error('[Atelier5Service] Auto-eco endpoint failed:', error.message);
      throw error;
    }
  }

  /**
   * Load synthesis data for an analysis
   * @param {string} analysisId - Analysis ID
   * @param {Object} options - Options for loading data
   * @returns {Promise<Object>} - Synthesis data response
   */
  async loadSynthesisData(analysisId) {
    try {
      // Try to load from the correct risk scenarios endpoint
      const response = await api.get(`/analyses/${analysisId}/atelier5/risk-scenarios`);

      const data = response.data || {};

      return { success: true, data };
    } catch (error) {
      console.warn(`[Atelier5Service] Failed to load synthesis data: ${error.message}`);

      // Try fallback endpoint
      try {
        const fallbackResponse = await api.get(`/analyses/${analysisId}/atelier5-synthesis`);
        const fallbackData = fallbackResponse.data || {};
        return { success: true, data: fallbackData };
      } catch (fallbackError) {
        console.warn(`[Atelier5Service] Fallback endpoint also failed: ${fallbackError.message}`);
      }

      // Return empty data structure when endpoints are not available
      const emptyData = {
        strategicScenarios: [],
        operationalScenarios: [],
        ecosystemMeasures: [],
        riskScenarios: {
          strategic: [],
          operational: [],
          crossReferences: {
            byBusinessValue: new Map(),
            bySupportingAsset: new Map(),
            byDreadedEvent: new Map(),
            byRiskSource: new Map()
          }
        },
        treatmentStrategies: {
          decisions: [],
          gaps: [],
          conflicts: []
        },
        securityMeasures: {
          measures: [],
          optimization: {
            recommendedCombinations: []
          }
        },
        residualRisks: {
          calculations: [],
          portfolioAnalysis: {
            totalRiskReduction: 0,
            highestRemainingRisks: [],
            recommendedActions: []
          }
        }
      };
      return { success: true, data: emptyData };
    }
  }

  /**
   * Fetches the BASE data constructed from Workshops 1-4.
   * This gives us the most up-to-date scenario definitions.
   */
  /**
   * Save ONLY Risk Scenario tab data (measures, attack path treatments, residual risks).
   * Uses PATCH endpoint that merges into existing data without touching non-tab fields.
   * @param {string} analysisId - Analysis ID
   * @param {Object} data - Tab-specific data { strategicScenarios: [...] }
   * @returns {Promise<Object>} - Response
   */
  async saveRiskScenarioTabData(analysisId, data) {
    try {
      const response = await api.patch(`/analyses/${analysisId}/workshop5-risk-scenario-tab`, data);
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      console.error('[Atelier5Service] Error saving risk scenario tab data:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save simplified Workshop 5 data with measure types and dehydration
   * @param {string} analysisId - Analysis ID
   * @param {Object} data - Workshop 5 data to save
   * @returns {Promise<Object>} - Response
   */
  async saveWorkshop5Data(analysisId, data) {
    try {
      console.log('💾 [Atelier5Service] Saving simplified Workshop 5 data:', {
        analysisId,
        strategicScenarios: data.strategicScenarios?.length || 0,
        totalCustomMeasures: data.strategicScenarios?.reduce((total, s) => total + (s.customMeasures?.length || 0), 0) || 0,
        totalDreadedEventMeasures: data.strategicScenarios?.reduce((total, s) => total + (s.dreadedEventMeasures?.length || 0), 0) || 0,
        totalEcosystemMeasures: data.strategicScenarios?.reduce((total, s) => total + (s.ecosystemMeasures?.length || 0), 0) || 0
      });

      // Create simplified data structure with ALL measure types and positions
      const simplifiedData = {
        strategicScenarios: data.strategicScenarios?.map(scenario => {
          console.log(`💾 [Atelier5Service] Processing scenario ${scenario.id}:`, {
            name: scenario.name,
            customMeasuresCount: scenario.customMeasures?.length || 0,
            dreadedEventMeasuresCount: scenario.dreadedEventMeasures?.length || 0,
            ecosystemMeasuresCount: scenario.ecosystemMeasures?.length || 0,
            hasRiskTreatment: !!scenario.riskTreatment,
            hasResidualRisk: !!scenario.residualRisk
          });

          return {
            id: scenario.id,
            name: scenario.name,
            customMeasures: scenario.customMeasures?.map(measure => ({
              id: measure.id,
              name: measure.name,
              description: measure.description,
              category: measure.category,
              priority: measure.priority,
              effectiveness: measure.effectiveness,
              responsiblePerson: measure.responsiblePerson,
              implementation: measure.implementation,
              deadline: measure.deadline,
              cost: measure.cost || measure.costComplexity,
              budget: measure.budget,
              obstacles: measure.obstacles,
              estimatedEffort: measure.estimatedEffort,
              attackPaths: measure.attackPaths,
              attackPathId: measure.attackPathId,
              // Temporal position: immediate (+0), shortTerm (+3), mediumTerm (+6)
              temporalPosition: measure.temporalPosition || 'immediate',
              position: measure.position || { x: 0, y: 0 },
              source: 'custom',
              createdAt: measure.createdAt,
              updatedAt: new Date()
            })) || [],

            // DEHYDRATED: Dreaded Event Measures (Atelier 1) - store only references
            // The backend will hydrate these from AnalysisControl on load
            dreadedEventMeasures: scenario.dreadedEventMeasures?.map(measure => {
              if (measure.id === scenario.dreadedEventMeasures?.[0]?.id) {
                console.log(`[DEBUG W5] Sending dreadedEventMeasure id=${measure.id} name="${measure.name}" title="${measure.title}"`);
              }
              return {
              // Reference ID (required for hydration)
              id: measure.id,
              name: measure.name,
              title: measure.title,
              // W5 specific metadata
              temporalPosition: measure.temporalPosition || 'immediate',
              position: measure.position || { x: 0, y: 0 },
              attackPaths: measure.attackPaths,
              attackPathId: measure.attackPathId,
              retained: measure.retained,
              // Decision metadata
              decision: measure.decision,
              decisionDate: measure.decisionDate,
              // Tracking
              scenarioId: scenario.id,
              source: 'atelier1'
            }}) || [],

            // DEHYDRATED: Ecosystem Measures - store only references
            // The backend will hydrate these from ecosystem-measures on load
            ecosystemMeasures: scenario.ecosystemMeasures?.map(measure => ({
              // Reference ID (required for hydration)
              id: measure.id,
              name: measure.name,
              title: measure.title,
              // W5 specific metadata  
              temporalPosition: measure.temporalPosition || 'immediate',
              position: measure.position || { x: 0, y: 0 },
              attackPaths: measure.attackPaths,
              attackPathId: measure.attackPathId,
              retained: measure.retained,
              // Tracking
              scenarioId: scenario.id,
              source: 'ecosystem'
            })) || [],
            // Risk treatment
            riskTreatment: scenario.riskTreatment ? {
              option: scenario.riskTreatment.option,
              justification: scenario.riskTreatment.justification,
              responsiblePerson: scenario.riskTreatment.responsiblePerson,
              budget: scenario.riskTreatment.budget,
              implementation: scenario.riskTreatment.implementation,
              monitoringPlan: scenario.riskTreatment.monitoringPlan,
              updatedAt: new Date()
            } : null,
            // Residual risk (including risk acceptance data)
            residualRisk: scenario.residualRisk ? {
              initialRisk: scenario.residualRisk.initialRisk,
              residualRisk: scenario.residualRisk.residualRisk,
              measuresApplied: scenario.residualRisk.measuresApplied,
              riskReduction: scenario.residualRisk.riskReduction,
              // Include risk acceptance data
              riskAcceptance: scenario.residualRisk.riskAcceptance ? {
                criteria: scenario.residualRisk.riskAcceptance.criteria,
                riskOwner: scenario.residualRisk.riskAcceptance.riskOwner,
                acceptanceDate: scenario.residualRisk.riskAcceptance.acceptanceDate,
                reviewDate: scenario.residualRisk.riskAcceptance.reviewDate,
                approvalWorkflow: scenario.residualRisk.riskAcceptance.approvalWorkflow,
                acceptanceJustification: scenario.residualRisk.riskAcceptance.acceptanceJustification,
                acceptanceStatus: scenario.residualRisk.riskAcceptance.acceptanceStatus,
                riskScore: scenario.residualRisk.riskAcceptance.riskScore
              } : null,
              // Keep legacy fields for backward compatibility
              acceptanceStatus: scenario.residualRisk.acceptanceStatus,
              acceptanceJustification: scenario.residualRisk.acceptanceJustification,
              updatedAt: new Date()
            } : null,
            // Keep temporal evolution for measure positions
            temporalEvolution: scenario.temporalEvolution,
            attackPathTreatments: scenario.attackPathTreatments || {},
            attackPathResidualRisks: scenario.attackPathResidualRisks || {}
          };
        }) || []
      };

      console.log('📊 [Atelier5Service] Simplified data summary:', {
        scenarios: simplifiedData.strategicScenarios.length,
        customMeasures: simplifiedData.strategicScenarios.reduce((total, s) => total + (s.customMeasures?.length || 0), 0),
        dreadedEventMeasures: simplifiedData.strategicScenarios.reduce((total, s) => total + (s.dreadedEventMeasures?.length || 0), 0),
        ecosystemMeasures: simplifiedData.strategicScenarios.reduce((total, s) => total + (s.ecosystemMeasures?.length || 0), 0),
        riskTreatments: simplifiedData.strategicScenarios.filter(s => s.riskTreatment).length,
        residualRisks: simplifiedData.strategicScenarios.filter(s => s.residualRisk).length,
        riskAcceptances: simplifiedData.strategicScenarios.filter(s => s.residualRisk?.riskAcceptance).length
      });

      // Debug ecosystem measures
      simplifiedData.strategicScenarios.forEach((scenario, index) => {
        if (scenario.ecosystemMeasures && scenario.ecosystemMeasures.length > 0) {
          console.log(`🔍 [DEBUG] Scenario ${index + 1} (${scenario.id}) has ${scenario.ecosystemMeasures.length} ecosystem measures:`, scenario.ecosystemMeasures);
        }
      });

      // Use the simplified endpoint
      console.log('🚀 [ATELIER5 DEBUG] About to make API call to save simplified data...');
      console.log('📋 [ATELIER5 DEBUG] Data being sent:', {
        endpoint: `/analyses/${analysisId}/workshop5-simplified`,
        scenarios: simplifiedData.strategicScenarios.length,
        totalCustomMeasures: simplifiedData.strategicScenarios.reduce((total, s) => total + (s.customMeasures?.length || 0), 0),
        totalDreadedEventMeasures: simplifiedData.strategicScenarios.reduce((total, s) => total + (s.dreadedEventMeasures?.length || 0), 0),
        totalEcosystemMeasures: simplifiedData.strategicScenarios.reduce((total, s) => total + (s.ecosystemMeasures?.length || 0), 0),
        firstScenarioCustomMeasures: simplifiedData.strategicScenarios[0]?.customMeasures?.length || 0
      });

      const response = await api.post(`/analyses/${analysisId}/workshop5-simplified`, simplifiedData);

      return {
        success: true,
        data: response.data?.data || response.data,
        message: response.data?.message || 'Simplified Workshop 5 data saved successfully'
      };
    } catch (error) {
      console.error('❌ [Atelier5Service] Error saving simplified Workshop 5 data:', error.response?.data?.message || error.message);

      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 [Atelier5Service] Development mode - simulating successful save');
        console.log('📋 [DEV DEBUG] Data that WOULD have been saved:', {
          analysisId,
          scenariosCount: data.strategicScenarios?.length || 0,
          totalCustomMeasures: data.strategicScenarios?.reduce((total, s) => total + (s.customMeasures?.length || 0), 0) || 0,
          firstScenarioData: data.strategicScenarios?.[0] ? {
            id: data.strategicScenarios[0].id,
            name: data.strategicScenarios[0].name,
            customMeasuresCount: data.strategicScenarios[0].customMeasures?.length || 0,
            customMeasures: data.strategicScenarios[0].customMeasures || [],
            hasRiskTreatment: !!data.strategicScenarios[0].riskTreatment,
            hasResidualRisk: !!data.strategicScenarios[0].residualRisk
          } : null
        });
        return { success: true, data };
      }

      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  }

  /**
   * Fetches the ENHANCEMENT data saved specifically in Workshop 5.
   * This includes custom measures, risk treatments, and residual risks.
   */
  async loadWorkshop5Data(analysisId) {
    try {
      console.log('🚀🚀🚀 [Atelier5Service] === STARTING WORKSHOP 5 DATA FETCH ===');
      console.log('🚀🚀🚀 [Atelier5Service] Analysis ID:', analysisId);
      console.warn('🚀🚀🚀 [Atelier5Service] SERVICE IS BEING CALLED - CHECK THIS LOG');

      // Try endpoints in order of preference
      const endpoints = [
        `/analyses/${analysisId}/workshop5`,
        `/analyses/${analysisId}/workshop5-simplified`,
        `/analyses/${analysisId}/workshop5-data`,
        `/analyses/${analysisId}/atelier5-synthesis`
      ];

      for (const endpoint of endpoints) {
        try {
          console.log('🚀 [Atelier5Service] Trying endpoint:', endpoint);
          const response = await api.get(endpoint);

          console.log('🔍 [SERVICE DEBUG] Raw response from', endpoint, ':', {
            hasResponse: !!response,
            hasData: !!response.data,
            responseKeys: response.data ? Object.keys(response.data) : [],
            success: response.data?.success,
            hasDataProperty: !!response.data?.data,
            dataKeys: response.data?.data ? Object.keys(response.data.data) : [],
            firstScenario: response.data?.data?.strategicScenarios?.[0] ? {
              id: response.data.data.strategicScenarios[0].id,
              name: response.data.data.strategicScenarios[0].name,
              hasCustomMeasures: !!(response.data.data.strategicScenarios[0].customMeasures?.length),
              hasRiskTreatment: !!response.data.data.strategicScenarios[0].riskTreatment,
              hasResidualRisk: !!response.data.data.strategicScenarios[0].residualRisk
            } : null
          });

          // Check if we have data in the expected format
          if (response.data?.success && response.data?.data && response.data.data.strategicScenarios?.length > 0) {
            console.log('✅ SERVICE: Fetched W5 data from', endpoint, ':', {
              scenarios: response.data.data.strategicScenarios?.length || 0,
              customMeasures: response.data.data.strategicScenarios?.reduce((total, s) => total + (s.customMeasures?.length || 0), 0) || 0,
              riskTreatments: response.data.data.strategicScenarios?.filter(s => s.riskTreatment).length || 0,
              residualRisks: response.data.data.strategicScenarios?.filter(s => s.residualRisk).length || 0,
              riskAcceptances: response.data.data.strategicScenarios?.filter(s => s.residualRisk?.riskAcceptance).length || 0
            });

            // Debug: Log risk acceptance data found in service
            response.data.data.strategicScenarios?.forEach((scenario, index) => {
              if (scenario.residualRisk?.riskAcceptance) {
                console.log(`🎯 [SERVICE] Scenario ${index + 1} has risk acceptance:`, {
                  id: scenario.id,
                  acceptanceStatus: scenario.residualRisk.riskAcceptance.acceptanceStatus,
                  riskOwner: scenario.residualRisk.riskAcceptance.riskOwner,
                  hasJustification: !!(scenario.residualRisk.riskAcceptance.justification || scenario.residualRisk.riskAcceptance.acceptanceJustification)
                });
              }
            });
            return { success: true, data: response.data.data };
          }

          // Check for direct data format (without success wrapper)
          if (response.data && response.data.strategicScenarios?.length > 0) {
            const dataStats = {
              scenarios: response.data.strategicScenarios?.length || 0,
              customMeasures: response.data.strategicScenarios?.reduce((total, s) => total + (s.customMeasures?.length || 0), 0) || 0,
              riskTreatments: response.data.strategicScenarios?.filter(s => s.riskTreatment).length || 0,
              residualRisks: response.data.strategicScenarios?.filter(s => s.residualRisk).length || 0,
              riskAcceptances: response.data.strategicScenarios?.filter(s => s.residualRisk?.riskAcceptance).length || 0
            };

            console.log('✅ SERVICE: Fetched W5 data from', endpoint, '(direct format):', dataStats);

            // Debug: Log risk acceptance data found in service (direct format)
            response.data.strategicScenarios?.forEach((scenario, index) => {
              if (scenario.residualRisk?.riskAcceptance) {
                console.log(`🎯 [SERVICE DIRECT] Scenario ${index + 1} has risk acceptance:`, {
                  id: scenario.id,
                  acceptanceStatus: scenario.residualRisk.riskAcceptance.acceptanceStatus,
                  riskOwner: scenario.residualRisk.riskAcceptance.riskOwner,
                  hasJustification: !!(scenario.residualRisk.riskAcceptance.justification || scenario.residualRisk.riskAcceptance.acceptanceJustification)
                });
              }
            });
            console.log('🔍 SERVICE: Returning data structure:', {
              success: true,
              hasData: !!response.data,
              dataKeys: Object.keys(response.data),
              firstScenarioId: response.data.strategicScenarios[0]?.id
            });

            const returnValue = { success: true, data: response.data };
            console.log('🔍 SERVICE: Final return value:', {
              success: returnValue.success,
              hasData: !!returnValue.data,
              dataType: typeof returnValue.data,
              dataKeys: returnValue.data ? Object.keys(returnValue.data) : []
            });

            return returnValue;
          }

          console.log('⚠️ [SERVICE] No data found in', endpoint);
        } catch (endpointError) {
          console.log('❌ [SERVICE] Endpoint', endpoint, 'failed:', endpointError.message);
          continue; // Try next endpoint
        }
      }

      // If we get here, no endpoint had data
      console.log('⚠️ [SERVICE] No W5 data found in any endpoint');
      const emptyReturn = { success: true, data: null };
      console.log('🔍 SERVICE: Returning empty result:', emptyReturn);
      return emptyReturn;
    } catch (error) {
      // A general error occurred
      console.error('❌ [Service] Error loading Workshop 5 data:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data
      });
      return { success: true, data: null }; // Return success with null data
    }
  }

  /**
   * Save temporal evolution for a specific scenario
   * @param {string} analysisId - Analysis ID
   * @param {string} scenarioId - Scenario ID
   * @param {Object} temporalEvolution - Temporal evolution data
   * @returns {Promise<Object>} - Response
   */
  async saveTemporalEvolution(analysisId, scenarioId, temporalEvolution) {
    try {
      console.log('📅 [SERVICE TEMPORAL] Saving temporal evolution for scenario:', scenarioId);

      const response = await api.post(
        `/analyses/${analysisId}/workshop5/${scenarioId}/temporal-evolution`,
        temporalEvolution
      );

      console.log('✅ [SERVICE TEMPORAL] Save response received');

      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ [SERVICE TEMPORAL] Error saving temporal evolution:', error.message);

      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 [SERVICE TEMPORAL] Development mode - simulating successful save');
        return { success: true, data: temporalEvolution };
      }

      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

  /**
   * Save residual risk reformulation for a specific scenario
   * @param {string} analysisId - Analysis ID
   * @param {string} scenarioId - Scenario ID
   * @param {Object} residualRisk - Residual risk data
   * @returns {Promise<Object>} - Response
   */
  async saveResidualRisk(analysisId, scenarioId, residualRisk) {
    try {
      console.log('⚠️ [SERVICE RESIDUAL RISK] Saving for scenario:', scenarioId);

      const response = await api.post(
        `/analyses/${analysisId}/workshop5/${scenarioId}/residual-risk`,
        residualRisk
      );

      console.log('✅ [SERVICE RESIDUAL RISK] Save response received');

      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ [SERVICE RESIDUAL RISK] Error saving residual risk:', error.message);

      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 [SERVICE RESIDUAL RISK] Development mode - simulating successful save');
        return { success: true, data: residualRisk };
      }

      return { success: false, error: error.response?.data?.message || error.message };
    }
  }

}

// Create and export a singleton instance
const atelier5Service = new Atelier5Service();

// Export both the class and the instance for different use cases
export { Atelier5Service };
export default atelier5Service;
